// SPDX-License-Identifier: Apache-2.0
// File: src/ui/components/FineTuneSoundModal.tsx

import React, { useState } from 'react';
import type { SynthPreset } from '../../presets/FactorySoundLibrary';
import { PocketGridRetroPalette } from '../theme/PocketGridRetroTheme';
import { synthEngine } from '../../audio/AudioEngineController';

interface FineTuneSoundModalProps {
  preset: SynthPreset;
  targetLabel?: string;
  onApplyChanges?: (updatedPreset: SynthPreset) => void;
  onSaveToPreset?: (updatedPreset: SynthPreset) => void;
  onSaveToUserSlot?: (updatedPreset: SynthPreset) => void;
  onClose: () => void;
}

export const FineTuneSoundModal: React.FC<FineTuneSoundModalProps> = ({
  preset,
  targetLabel,
  onApplyChanges,
  onSaveToPreset,
  onSaveToUserSlot,
  onClose,
}) => {
  const [currentPatch, setCurrentPatch] = useState<SynthPreset>({
    ...preset,
    params: { ...preset.params },
  });

  const [fineTuneCents, setFineTuneCents] = useState<number>(0); // -50..+50 cents
  const [velocityCurve, setVelocityCurve] = useState<'LINEAR' | 'SOFT' | 'HARD' | 'FIXED'>('LINEAR');
  const [stereoPan, setStereoPan] = useState<number>(0); // -100..+100%
  const [portamentoMs, setPortamentoMs] = useState<number>(0); // 0..1500 ms

  const updateParam = (key: string, val: number) => {
    const updated = {
      ...currentPatch,
      params: {
        ...currentPatch.params,
        [key]: Math.max(0.0, Math.min(1.0, val)),
      },
    };
    setCurrentPatch(updated);
    synthEngine.setParameter(key as any, val);
  };

  const auditionNote = (note: number) => {
    void synthEngine.noteOn(
      note,
      0.88,
      8888,
      1,
      currentPatch.targetEngineId,
      currentPatch.opRatios,
      currentPatch.opLevels,
      currentPatch.params
    );
    window.setTimeout(() => synthEngine.noteOff(note, 8888), 350);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(10, 11, 13, 0.94)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '820px',
          maxHeight: 'min(94dvh, calc(100vh - 12px))',
          height: '100%',
          background: '#181A1D',
          border: `1.5px solid ${PocketGridRetroPalette.panelBorder}`,
          borderRadius: '6px',
          boxShadow: '0 20px 48px rgba(0,0,0,0.95)',
          display: 'flex',
          flexDirection: 'column',
          color: PocketGridRetroPalette.warmIvoryText,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Sticky Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '4px 10px',
            borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            background: '#1D1F23',
            flexShrink: 0,
            minHeight: '32px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <span style={{ fontSize: '13px' }}>🔧</span>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.incandescentCream,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                FINE TUNE SOUND: {currentPatch.name}
              </div>
              <div style={{ fontSize: '8.5px', color: PocketGridRetroPalette.mutedPewterText }}>
                {currentPatch.category} • {currentPatch.engineBadge}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
              onClick={() => auditionNote(currentPatch.category === 'BASSES' ? 38 : 62)}
              title="Audition current fine-tuned sound"
              style={{
                height: '24px',
                padding: '0 9px',
                borderRadius: '4px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: 'none',
                color: '#FFF',
                fontWeight: 800,
                fontSize: '10px',
                cursor: 'pointer',
              }}
            >
              ▶ AUDITION
            </button>
            <button
              onClick={onClose}
              style={{
                height: '24px',
                padding: '0 8px',
                borderRadius: '4px',
                background: '#282A2E',
                border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 700,
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable 2-Column Content Body */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '6px 8px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '6px',
            alignContent: 'start',
          }}
        >
          {/* Module 1: Pitch & Tuning */}
          <div style={{ background: '#131416', padding: '4px 8px', borderRadius: '5px', border: '1px solid #282A2E' }}>
            <div style={{ fontSize: '9.5px', fontWeight: 800, color: PocketGridRetroPalette.tungstenAmber, marginBottom: '4px', letterSpacing: '0.04em' }}>
              1. PITCH CALIBRATION & PORTAMENTO
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px' }}>
              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  MASTER FINE TUNE: {fineTuneCents >= 0 ? `+${fineTuneCents}` : fineTuneCents} ct
                </div>
                <input
                  type="range"
                  min="-50"
                  max="50"
                  value={fineTuneCents}
                  onChange={(e) => {
                    const c = Number(e.target.value);
                    setFineTuneCents(c);
                    synthEngine.sendGlobalPitchBend(c / 100);
                  }}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.tungstenAmber }}
                />
              </div>

              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  PORTAMENTO / GLIDE: {portamentoMs} ms
                </div>
                <input
                  type="range"
                  min="0"
                  max="1500"
                  value={portamentoMs}
                  onChange={(e) => {
                    const ms = Number(e.target.value);
                    setPortamentoMs(ms);
                    updateParam('portamento', ms / 1500);
                  }}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.tungstenAmber }}
                />
              </div>

              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  STEREO PAN: {stereoPan === 0 ? 'CENTER' : stereoPan < 0 ? `L ${Math.abs(stereoPan)}%` : `R ${stereoPan}%`}
                </div>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  value={stereoPan}
                  onChange={(e) => setStereoPan(Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.tungstenAmber }}
                />
              </div>
            </div>
          </div>

          {/* Module 2: Acoustic Body & Filter Brightness */}
          <div style={{ background: '#131416', padding: '6px 10px', borderRadius: '5px', border: '1px solid #282A2E' }}>
            <div style={{ fontSize: '10px', fontWeight: 800, color: PocketGridRetroPalette.vintageVfdSage, marginBottom: '6px', letterSpacing: '0.04em' }}>
              2. ACOUSTIC TIMBRE & FILTER SCULPTING
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px' }}>
              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  TONE (CUTOFF): {Math.round((currentPatch.params.cutoff ?? 0.75) * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={currentPatch.params.cutoff ?? 0.75}
                  onChange={(e) => updateParam('cutoff', Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.vintageVfdSage }}
                />
              </div>

              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  BODY RESONANCE: {Math.round((currentPatch.params.resonance ?? 0.15) * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={currentPatch.params.resonance ?? 0.15}
                  onChange={(e) => updateParam('resonance', Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.vintageVfdSage }}
                />
              </div>

              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  SOUNDBOARD WARMTH: {Math.round((currentPatch.params.drive ?? 0.1) * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={currentPatch.params.drive ?? 0.1}
                  onChange={(e) => updateParam('drive', Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.vintageVfdSage }}
                />
              </div>

              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  HAMMER / ATTACK SNAP: {Math.round((currentPatch.params.modDecay ?? 0.3) * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={currentPatch.params.modDecay ?? 0.3}
                  onChange={(e) => updateParam('modDecay', Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.vintageVfdSage }}
                />
              </div>
            </div>
          </div>

          {/* Module 3: Dynamic Touch & Velocity Response */}
          <div style={{ background: '#131416', padding: '6px 10px', borderRadius: '5px', border: '1px solid #282A2E' }}>
            <div style={{ fontSize: '10px', fontWeight: 800, color: PocketGridRetroPalette.burntTerracotta, marginBottom: '6px', letterSpacing: '0.04em' }}>
              3. VELOCITY DYNAMICS & TOUCH SENSITIVITY
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {(['LINEAR', 'SOFT', 'HARD', 'FIXED'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setVelocityCurve(mode)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    background: velocityCurve === mode ? PocketGridRetroPalette.burntTerracotta : '#202226',
                    border: `1px solid ${velocityCurve === mode ? PocketGridRetroPalette.burntTerracotta : '#383A40'}`,
                    color: velocityCurve === mode ? '#FFF' : PocketGridRetroPalette.warmIvoryText,
                    fontSize: '10px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {mode === 'LINEAR' ? 'Standard Linear' : mode === 'SOFT' ? 'Soft Expressive' : mode === 'HARD' ? 'Hard Punchy' : 'Fixed 127'}
                </button>
              ))}
            </div>
          </div>

          {/* Module 4: Spatial Bloom & Ambience */}
          <div style={{ background: '#131416', padding: '6px 10px', borderRadius: '5px', border: '1px solid #282A2E' }}>
            <div style={{ fontSize: '10px', fontWeight: 800, color: PocketGridRetroPalette.champagneBrass, marginBottom: '6px', letterSpacing: '0.04em' }}>
              4. SPATIAL AMBIENCE & CONCERT HALL REVERB
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px' }}>
              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  HALL REVERB BLOOM: {Math.round((currentPatch.params.spaceReverbMix ?? 0.25) * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={currentPatch.params.spaceReverbMix ?? 0.25}
                  onChange={(e) => updateParam('spaceReverbMix', Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.champagneBrass }}
                />
              </div>

              <div>
                <div style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText, marginBottom: '2px' }}>
                  CHORUS / ENSEMBLE: {Math.round((currentPatch.params.chorusDelayMix ?? 0.1) * 100)}%
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={currentPatch.params.chorusDelayMix ?? 0.1}
                  onChange={(e) => updateParam('chorusDelayMix', Number(e.target.value))}
                  style={{ width: '100%', accentColor: PocketGridRetroPalette.champagneBrass }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer Bar (Always Visible!) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '4px 10px',
            borderTop: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            background: '#1D1F23',
            flexShrink: 0,
            minHeight: '30px',
          }}
        >
          <button
            onClick={() => {
              setCurrentPatch({ ...preset });
              synthEngine.loadPreset(preset);
            }}
            style={{
              padding: '3px 8px',
              borderRadius: '4px',
              background: '#282A2E',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              color: PocketGridRetroPalette.mutedPewterText,
              fontWeight: 700,
              fontSize: '9.5px',
              cursor: 'pointer',
            }}
          >
            RESET DEFAULT
          </button>

          <div style={{ display: 'flex', gap: '5px' }}>
            {onSaveToUserSlot && (
              <button
                onClick={() => onSaveToUserSlot(currentPatch)}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  background: PocketGridRetroPalette.burntTerracotta,
                  border: 'none',
                  color: '#FFF',
                  fontWeight: 800,
                  fontSize: '9.5px',
                  cursor: 'pointer',
                }}
              >
                💾 SAVE SLOT
              </button>
            )}

            <button
              onClick={() => {
                if (onSaveToPreset) onSaveToPreset(currentPatch);
                if (onApplyChanges) onApplyChanges(currentPatch);
                onClose();
              }}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: 'none',
                color: '#FFF',
                fontWeight: 800,
                fontSize: '10.5px',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(78,154,103,0.4)',
              }}
            >
              ✓ APPLY & CLOSE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
